import { Box, Flex, Input } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { useActiveSection } from '../../../../queries/guides';
import {
  useActiveThemeset,
  useGetThemesetColors,
} from '../../../../queries/themesets';
import { useSectionMutations } from '../../../../mutations/sections';
import { IconImage, IconColor } from '@bynder/icons';
import React, { useState } from 'react';
import ImageInspectorPanelSelector from '../WidgetSettings/ImageInspectorPanelSelector';
import { getAssetUrl } from '../../../../constants/widgets';
import { CVAsset, CVFile } from '../../../../types';
import InspectorPanelColorPicker from '../ColorPicker';
import {
  defaultTransparent,
  defaultWhite,
} from '../../../widgets/RichText/Config';
import { getValueFromStyles, updateStyles } from '../helpers';
import { CLASSNAMES, CSS_PROPERTIES } from '../constants';
import { InspectorSelect } from '../InspectorSelect';
import { useDebouncedCallback } from 'use-debounce';
import { UploadType } from '../../../../hooks/useUploader';

enum BackgroundOption {
  SolidColor = 'solidColor',
  Image = 'image',
}

const DEFAULT_OPACITY = '20';

export const SectionBackgroundEditor = () => {
  const { activeSection } = useActiveSection();

  const customColors = [...useGetThemesetColors()];
  const { editSectionStyle } = useSectionMutations();
  const [selectedOption, setSelectedOption] = useState(
    activeSection?.asset?.id
      ? BackgroundOption.Image
      : BackgroundOption.SolidColor,
  );

  const activeOverlayColor = customColors.find(
    color =>
      color.hexCode ===
      getValueFromStyles(
        activeSection?.style.stylesheet,
        CSS_PROPERTIES.BACKGROUND_COLOR,
        `.${CLASSNAMES.SECTION_OVERLAY}`,
      ),
  );

  const handleColorPickerClick = (colorId: string) => {
    const selectedColor = customColors.find(color => color.id === colorId);

    editSectionStyle.mutate({
      sectionId: activeSection.id,
      style: updateStyles(
        activeSection.style.stylesheet,
        `.${CLASSNAMES.SECTION_OVERLAY} {
          ${CSS_PROPERTIES.BACKGROUND_COLOR}: ${
            selectedColor ? selectedColor.hexCode : defaultTransparent.hexCode
          };
          ${CSS_PROPERTIES.OPACITY}: ${
            getValueFromStyles(
              activeSection.style.stylesheet,
              CSS_PROPERTIES.OPACITY,
              `.${CLASSNAMES.SECTION_OVERLAY}`,
            ) || `${DEFAULT_OPACITY}%`
          };
        }`,
      ),
    });
  };

  return (
    <>
      <SectionBackgroundDropdown
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
      />

      {selectedOption === BackgroundOption.SolidColor && (
        <>
          <BackgroundColorEditor />
        </>
      )}
      {selectedOption === BackgroundOption.Image && (
        <>
          <BackgroundImageEditor />
        </>
      )}
      <Flex justifyContent="space-between" alignItems="center" gap="8">
        <div style={{ flexGrow: 1 }}>
          <InspectorPanelColorPicker
            handleClear={() => handleColorPickerClick(defaultTransparent.id)}
            defaultColorHex={defaultTransparent.hexCode}
            activeColor={activeOverlayColor}
            colors={customColors}
            handleClick={handleColorPickerClick}
            label="Overlay"
          />
        </div>

        <div style={{ width: '80px' }}>
          <OpacitySetting isColorPicked={!!activeOverlayColor} />
        </div>
      </Flex>
    </>
  );
};

const SectionBackgroundDropdown = ({ selectedOption, setSelectedOption }) => {
  const { translate } = useTranslate();

  const getItems = () => [
    {
      label: translate('SECTION_INSPECTOR.BACKGROUND_SOLID_COLOR'),
      value: BackgroundOption.SolidColor,
      key: 0,
      icon: <IconColor />,
    },
    {
      label: translate('SECTION_INSPECTOR.BACKGROUND_IMAGE'),
      value: BackgroundOption.Image,
      key: 1,
      icon: <IconImage />,
    },
  ];

  return (
    <Box>
      <InspectorSelect
        items={getItems()}
        selectedValue={selectedOption}
        handleChange={option => {
          setSelectedOption(option);
        }}
      />
    </Box>
  );
};

const BackgroundColorEditor = () => {
  const { activeSection } = useActiveSection();
  const customColors = useGetThemesetColors();
  const { data: themeset } = useActiveThemeset();
  const { translate } = useTranslate();
  const { changeBackgroundColor } = useSectionMutations();
  const handleEditBackgroundColor = (backgroundColorId: string) => {
    changeBackgroundColor.mutate({
      sectionId: activeSection?.id,
      backgroundColorId,
    });
  };

  const getDefaultColor = () => {
    if (themeset?.pageSection.backgroundColor) {
      return themeset?.pageSection.backgroundColor;
    }
    return defaultWhite;
  };

  const activeBackgroundColor = customColors.find(
    color => color.id === activeSection?.backgroundColorId,
  );
  return (
    <Box paddingTop="4">
      <InspectorPanelColorPicker
        testId="sectionBackgroundColor"
        defaultColorHex={getDefaultColor().hexCode}
        handleClear={() => handleEditBackgroundColor(null)}
        label={translate('THEMESET_GUIDE_UI_NAVIGATION_BAR.BACKGROUND_COLOR')}
        colors={customColors}
        activeColor={activeBackgroundColor}
        handleClick={handleEditBackgroundColor}
      />
    </Box>
  );
};

const BackgroundImageEditor = () => {
  const { activeSection } = useActiveSection();
  const {
    placeAssetInSectionBackground,
    removeAssetFromSectionBackground,
    uploadAssetInSectionBackground,
  } = useSectionMutations();

  const onAssetSelect = (selectedMedia: CVAsset[], file: CVFile) => {
    const {
      databaseId,
      name,
      url: imageAssetUrl,
      extensions,
      derivatives,
    } = selectedMedia[0];

    placeAssetInSectionBackground.mutate({
      sectionId: activeSection?.id,
      assetId: databaseId,
      assetName: name,
      detailUrl: imageAssetUrl,
      imageUrl: getAssetUrl(extensions[0], file, derivatives.webImage),
      extension: extensions[0],
    });
  };

  const onCropAsset = (croppedUrl: string) => {
    placeAssetInSectionBackground.mutate({
      sectionId: activeSection?.id,
      assetId: activeSection.asset.id,
      assetName: activeSection.asset.name,
      detailUrl: activeSection.asset.detailUrl,
      imageUrl: croppedUrl,
      extension: activeSection.asset.extension,
    });
  };

  const onClearImage = () => {
    removeAssetFromSectionBackground.mutate({ sectionId: activeSection?.id });
  };

  const handleUploadCompleted = (mediaId: string) => {
    uploadAssetInSectionBackground.mutate({
      mediaId,
      sectionId: activeSection?.id,
    });
  };

  return (
    <ImageInspectorPanelSelector
      uploadType={UploadType.Background}
      handleUploadCompleted={handleUploadCompleted}
      handleAssetSelect={onAssetSelect}
      handleOnClearImage={onClearImage}
      handleOnSetImage={onCropAsset}
      imageUrl={activeSection?.asset?.imageUrl}
      assetName={activeSection?.asset?.name}
    />
  );
};

const OpacitySetting = ({ isColorPicked }: { isColorPicked: boolean }) => {
  const { activeSection } = useActiveSection();
  const { stylesheet } = useActiveSection().activeSection.style;
  const { editSectionStyle } = useSectionMutations();
  const styleOpacity = Number(
    getValueFromStyles(
      stylesheet,
      CSS_PROPERTIES.OPACITY,
      `.${CLASSNAMES.SECTION_OVERLAY}`,
    )?.replace('%', ''),
  );

  const [opacity, setOpacity] = useState(styleOpacity);

  React.useEffect(() => {
    setOpacity(styleOpacity);
  }, [stylesheet, styleOpacity]);
  const updateWidget = useDebouncedCallback(
    React.useCallback(
      (opacity: number) => {
        editSectionStyle.mutate({
          sectionId: activeSection.id,
          style: updateStyles(
            activeSection.style.stylesheet,
            `.${CLASSNAMES.SECTION_OVERLAY} {
          ${CSS_PROPERTIES.OPACITY}: ${opacity}%;
        }`,
          ),
        });
      },
      [activeSection, editSectionStyle],
    ),
    1000,
  );

  const handleChange = (opacity: string) => {
    setOpacity(Number(opacity));
    updateWidget(Number(opacity));
  };

  return (
    <Input
      isDisabled={!isColorPicked}
      value={String(opacity)}
      onChange={handleChange}
      type="number"
      suffix="%"
    />
  );
};
